import React, { Dispatch, SetStateAction, useRef } from "react";
import { useReactToPrint } from "react-to-print";
import { info } from 'console';
import { Code } from 'constants/codes';
import { Utility } from 'helpers/UtilityHelpers';
import { title } from 'process';
import { MessageType, Privilege } from 'models/BaseModel'
import { useNavigate } from "react-router-dom";
import { RefObject } from "react";
import { downloadPdf } from "components/common/SaveToPdf"; 
import { API } from "helpers/ApiHelpers";
import { Client } from "models/ClientModel";
import { Emailer } from "helpers/EmailerHelipers";

interface LinkButtonProps {
    id: string;
    title: string;
    info?: string;
    className?: string;
    onClick: () => void; // Now `onClick` is mandatory
}
  
const LinkButton: React.FC<LinkButtonProps> = ({
    id,
    info = '',
    className = '',
    title,
    onClick,
  }) => {
    return (
      <button type="button" id={id} onClick={onClick} className={`tbutton ${className}`} title={info}>
        {title}
      </button>
    );
};

interface ActionButtonProps {
  title: string;
  info?: string;
  className?: string;
  animation?: boolean;
  color?: number;
  visible?: boolean;
  enable?: boolean;
  onClick: () => Promise<void> | void;
}

const ActionButton: React.FC<ActionButtonProps> = ({
  title,
  info = '',
  className = '',
  animation = true,
  color = 0,
  visible = true,
  enable = true,
  onClick,
}) => {

  const handleClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
    if(animation) {
      const button = e.currentTarget;
      button.classList.add("spintext");

      try {
        await new Promise((resolve) => setTimeout(resolve, 500));
        await onClick(); // support both sync and async
      } finally {
        button.classList.remove("spintext");
      }
    } else { onClick(); }
  };

  if(!visible) return null;
    
  return (
   enable ?
      <button 
        type="button" 
        onClick={handleClick} 
        className={`tbutton bcolor${color} ${className? className : ''}`} 
        title={info}>
        {title}
      </button>
      :
      <button 
        type="button" 
        className={`tbutton disabled ${className? className : ''}`} 
        title={'No privileges to execute this action.'}
        disabled={true}>
        {title}
      </button>
  );
};

interface AutoGenerateButtonProps {
  keyId?: string;
  title: string;
  amount?: number;
  IdValue?: { id: number, status: number };
  isOrderGenerated?: boolean;
  onGenerate: () => Promise<void> | void;
  onView: () => Promise<void> | void;
}

const AutoGenerateButton: React.FC<AutoGenerateButtonProps> = ({
  title,
  amount = '',
  IdValue = { id: 2, status: 2},
  isOrderGenerated = false,
  onGenerate,
  onView,
}) => {

  if(isOrderGenerated){
    IdValue  = IdValue ?? { id: 2, status: 2};
    const isGenerate = IdValue.id > 2;
    if(isGenerate){
      return (
        <Button.Action 
          title={title} 
          info={`View ${title}: $${amount}`} 
          onClick={onView} 
          color={0} 
          animation={false} 
        />
      )
    }
    else
    {
      let info = `Pending: $${amount} `;
      if(title==="Payment") 
        info += `(Payment can be added directly from the invoice only)`;
      else 
        info += `(Click here to generate ${title.toLocaleLowerCase()})`; 
      
      return (
        <Button.Action 
          title={title} 
          info={info} 
          onClick={onGenerate} 
          color={1} 
          animation={false} 
        />
      )
    }
  }
  else{
    return (
        <Button.Action 
          title={title} 
          info={`Disabled : Generate Order first to enable this button - ${title}: $${amount} pending`} 
          onClick={() =>(console.log("This option is disabled"))} 
          color={12} 
          animation={false}
        />
      )
  }
};

interface IconButtonProps {
  info?: string;
  animation?: boolean;
  className?: string;
  onClick: () => Promise<void> | void;
}

const IconButton: React.FC<IconButtonProps> = ({
  info = '',
  className = '',
  animation = true,
  onClick,
}) => {

  const handleClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
    if(animation)
    {
      const button = e.currentTarget;
      button.classList.add('spinner');

      try {
        await new Promise((resolve) => setTimeout(resolve, 500));
        await onClick(); 
      } finally {
        button.classList.remove('spinner');
      }
    }
    else { onClick(); }
    
  };

  return (
    <button 
      type="button" 
      onClick={handleClick} 
      className={`ibutton mr2 ${className? className : ''}`} 
      title={info}>
      &nbsp;
    </button>
  );
};

interface FrameActionButtonProps {
  keyId?: string;
  title: string;
  info?: string;
  size?: [number,number,number,string?] | null | undefined;
  className?: string;
  classButton?: string;
  disable?: boolean;
  visible?: boolean;
  onClick: () => void; 
  align?: typeof Code.Align[keyof typeof Code.Align];
}

const FrameActionButton: React.FC<FrameActionButtonProps> = ({
  keyId = '',
  title,
  info = '',
  size,
  className = 'tbutton',
  classButton = '',
  disable = false,
  visible = true,
  onClick,
  align = Code.Align.LEFT,
}) => {
  let frameClass = Utility.Get.alignClass(align);
  
  classButton += title == '+' ? ' pbutton' : ''
  
  if (keyId === Code.Key.NEW) return null;

  return (
    visible ? 
      <div className={`fbutton ${frameClass} ${className} ${size ? Utility.Convert.formColumnStyle(size) : "fl form-col100"}`}>
        <button type="button" onClick={onClick} className={`tbutton ${classButton? classButton : ''} ${disable ? 'bdisable':''}`} disabled={disable} title={`${disable ? '(Disabled)':''} ${info}`}>
          {title}
        </button>
      </div> : null
  );
};

interface CreateButtonProps {
  keyId?: string;
  title: string;
  info?: string;
  className?: string;
  Privilege: Privilege;
  onClick: () => void; 
}

const CreateButton: React.FC<CreateButtonProps> = ({
  keyId = '',
  title,
  info,
  className = '',
  Privilege,
  onClick,
}) => {

  title = Utility.Get.FormFullName(title);

  const formattedTitle = Utility.Convert.TitleCase(title?.toString() || '');

  if (keyId === Code.Key.NEW) return null; 

  return (
    <>
      { (Privilege.ALL || Privilege.CREATE) ? (
        <button 
          type="button" 
          onClick={onClick} 
          className={`tbutton ${className}`} 
          title={info ? info : `Create ${formattedTitle.toLowerCase()}`}
        >
          +&nbsp; {formattedTitle}
        </button>
      ) : (
        <button 
          type="button" 
          className={`tbutton disabled ${className} `} 
          title={`No privilege to create new ${formattedTitle.toLowerCase()}`} 
          disabled
        >
          +&nbsp; {formattedTitle}
        </button>
      )}
    </>
  );
};

interface DeleteButtonProps {
  onClick: () => void;
  activeId?: number;
  className?: string;
  Privilege: Privilege,
}

const DeleteButton: React.FC<DeleteButtonProps> = ({
  onClick,
  activeId = 2,
  Privilege,
}) => {
  const title = activeId === 2 ? "Delete" : "Restore";

  return (
    Privilege.ALL ||  Privilege.DELETE ? 
    <button 
      type="button" 
      id="btnDelete" 
      className={`ibutton ${activeId === 2 ? "bdelete" : "brestore"}`}  
      onClick={onClick} 
      title={`${title} selected records`}>&nbsp;</button>
    :
    <button 
      type="button" 
      id="btnDelete" 
      className={`ibutton disabled ${activeId === 2 ? "bdelete" : "brestore"}`} 
      title={`No privileges to ${title.toLowerCase()} records.`} 
      disabled={true}>&nbsp;</button>
  );
};

interface TopButtonsProps {
  formName?: string;
  children?: React.ReactNode;
}

const TopButtons: React.FC<TopButtonsProps> = ({ 
  formName='',
  children
}) => {
  const navigate = useNavigate();
  return (
    <div className="form-buttons">
      <div 
        className="print-head">
          {formName.toUpperCase()}
      </div>

      {children}

    </div>
)};

interface SaveButtonsProps {
  keyId: string;
  activeId: number | undefined;
  hasUnsaved?: boolean;
  title?: string;
  formName?: string;
  formId?: number | undefined;
  clientNumber?: string;
  userName?: string;
  Privilege: Privilege,
  print?: boolean,
  handleDelete: () => void;
  className?: string;
  children?: React.ReactNode;
}

const SaveButtons: React.FC<SaveButtonsProps> = ({ 
  keyId, 
  activeId, 
  hasUnsaved = true,
  title='',
  formName='',
  formId = 0,
  clientNumber,
  userName,
  Privilege,
  print = false,
  handleDelete,
  className='',
  children
}) => {
  const navigate = useNavigate();
  const formFullname = Utility.Get.FormFullName(formName);
  return (
    <div className={`form-buttons lcolor${formName} ${className}`}>
      {
        Privilege.ALL ||  Privilege.CREATE || Privilege.MODIFY ?
        <button 
          id='btnSave' 
          className={`tbutton ${hasUnsaved ? '' : 'bdisable'}`} 
          type='submit' 
          accessKey='s'
          disabled={!hasUnsaved}
          title={hasUnsaved ? `${keyId === Code.Key.NEW ? 'Create new record (Alt+S)' : 'Update this record (Alt+S)'}` : "You haven’t changed anything yet."}
          >
          {keyId === Code.Key.NEW ? 'Save' : 'Save'}
        </button>
        :
        <button 
          id='btnSave' 
          className='tbutton disabled' 
          title={`${keyId === Code.Key.NEW ? 'No privileges to create new record.' : 'No privileges to update this record.'}`}
          disabled={true}
          >
          {keyId === Code.Key.NEW ? 'Create' : 'Update'}
        </button>
      }

      {keyId !== Code.Key.NEW && print && (
        Privilege.ALL ||  Privilege.PRINT ?
        <button 
          id='btnPrint'
          className='ibutton bprint' 
          title={`Print this ${formFullname} (Alt+P)`} 
          type='button' 
          accessKey='p'
          onClick={() => navigate(`/${formName}/print/${keyId}`)}
          >
        </button>
        :
        <button 
          id='btnPrint'
          className='btnPrint bprint disabled'
          title={`No privileges to print this ${formFullname}`} 
          disabled={true}
          >
        </button>
      )}
      
      {keyId !== Code.Key.NEW && (
        Privilege.ALL ||  Privilege.DELETE ?
        <button 
          id='btnDelete'
          className={`ibutton ${activeId === 2 ? 'bdelete' : 'brestore'}`} 
          title={`${activeId === 2 ? 'Delete this record (Alt+L)' : 'Restore this record (Alt+R)'}`} 
          type='button' 
          accessKey={`${activeId === 2 ? 'l' : 'r'}`}
          onClick={handleDelete}
          >
        </button>
        :
        <button 
          id='btnDelete'
          className={`ibutton ${activeId === 2 ? 'bdelete disabled' : 'brestore disabled'}`} 
          title={`${activeId === 2 ? 'No privileges to delete this record' : 'No privileges to restore this record'}`} 
          disabled={true}
          >
        </button>
      )}

      <div className="form-head" dangerouslySetInnerHTML={{ __html: title }} />

      <div 
        className='form-desc' title='Form Name'>
        {userName ? userName : ''} 
        &nbsp; &nbsp; 
        <span className='copy' title='#Client Id'>{clientNumber ? `#${clientNumber}`: ''}</span> 
        &nbsp; &nbsp; 
        {formFullname.toUpperCase()}
      </div>

      {children}

    </div>
)};

interface PrintButtonsProps {
  keyId: string;
  formName: string;
  userName?: string
  formTitle?: string,
  clientData?: Client | null,
  signData?: string,
  Privilege: Privilege,
  children?: React.ReactNode;
  fileName: string | undefined,
  printRef: RefObject<HTMLDivElement>,
  signRequire?: boolean,
  showModify?: boolean,
  setMessage: Dispatch<SetStateAction<MessageType>>,
  setError: (error: string) => void
}

const PrintButtons: React.FC<PrintButtonsProps> = ({ 
  keyId, 
  formName,
  userName,
  formTitle,
  clientData,
  signData ='',
  Privilege,
  children,
  fileName = '',
  printRef,
  signRequire = false,
  showModify = true,
  setMessage,
  setError
}) => {
  const navigate = useNavigate();
  if(!formTitle){
    formTitle = Utility.Convert.TitleCase(formName.replace("-"," "))  
  } 
  formName = formName.replace("-","")
  const phoneNumber = clientData?.client_phone || clientData?.client_cell || clientData?.client_mobile || '';
  const faxNumber = clientData?.client_fax || '';
  const emailAddress = clientData?.client_email || '';

  const onCallClicked = () => {
    if(!Utility.isValid.Phone(phoneNumber)) {
      Utility.Message.Info([`Phone number is invalid or not available`], setMessage);
      return;
    }
    window.location.href = `tel:+1${phoneNumber}`;  
  };

  const onEmailClicked = () => {
    if(!Utility.isValid.Email(emailAddress)) {
      Utility.Message.Info([`Email id is invalid or not available`], setMessage);
      return;
    }

    const companyName = Utility.Storage.CompanyName();
    const titleData = `${Utility.Convert.TitleCase(formName)} Attached`;
    
    const contentData = Emailer.Basic.Attach({
      title: titleData,
      companyName: companyName,
      content: Emailer.Basic.Content(clientData?.client_first_name!,formName,companyName)});

    API.Data.sendAsEmail(printRef, emailAddress, titleData, contentData, setMessage, setError);
  };



  const onFaxClicked = () => {
    if(!Utility.isValid.Phone(faxNumber)) {
      Utility.Message.Info([`Fax number is invalid or not available`], setMessage);
      return;
    }   

    const companyName = Utility.Storage.CompanyName();
    const titleData = `${Utility.Convert.TitleCase(formName)} Attached`;
    
    const contentData = Emailer.Basic.Attach({
      title: titleData,
      companyName: companyName,
      content: Emailer.Basic.Content(clientData?.client_first_name!,formName,companyName)});

    API.Data.sendAsEmail(printRef, `1${faxNumber}@srfax.com`, titleData, contentData, setMessage, setError);
  };



  const onSaveDocument = () => {
    console.log("signData.length==========>",signData.length);
    if(signRequire && signData.length<10) {
      Utility.Message.Error([`Client Signature required!`], setMessage);
      return;
    }   
    API.Data.saveToDocument(printRef, Code.Form.Id.CLIENT, clientData?.client_id, Utility.Get.DocumentType(formName), setMessage, setError);
  };
  
  return (
    <div className="print-buttons">
      
      {showModify && keyId !== Code.Key.NEW && (
        Privilege.ALL ||  Privilege.MODIFY ?
        <button 
          id="btnModify"
          className="tbutton" 
          title={`Modify this ${formTitle} (Alt+S)`}
          type="button" 
          accessKey="m"
          onClick={() => navigate(`/${formName}/${keyId}`)}
          >Modify
        </button>
        :
        <button 
          id="btnModify"
          className={`tbutton disabled`} 
          title={`No privileges to modify this ${formTitle}`} 
          disabled={true}
          >
        </button>
      )}

      {
        Privilege.ALL ||  Privilege.PRINT ?
        <button 
          id="btnPrint" 
          className="ibutton bprint" 
          accessKey="p"
          title="Print Preview (Alt+P)"
          onClick={window.print}
        ></button>
        :
        <button 
          id="btnPrint" 
          className="ibutton bprint disabled" 
          title={`No privileges to print this ${formTitle}.`}
          disabled={true}
          ></button>
      }

      {
        emailAddress && emailAddress.length>0 && (Privilege.ALL ||  Privilege.EMAIL ?
        <button 
          id="btnEmail" 
          className="ibutton bemail" 
          accessKey="e"
          title="Email (Alt+E)"
          onClick={onEmailClicked}
        ></button>
        :
        <button 
          id="btnEmail" 
          className="ibutton bemail disabled" 
          title={`No privileges to email this ${formTitle}.`}
          disabled={false}
          ></button>) 
      }

      {
        faxNumber && faxNumber.length==10 && (Privilege.ALL ||  Privilege.CALL ?
        <button 
          id="btnFax" 
          className="ibutton bfax" 
          accessKey="f"
          title="Fax (Alt+F)"
          onClick={onFaxClicked}
        ></button>
        :
        <button 
          id="btnFax" 
          className="ibutton bfax disabled" 
          title={`No privileges to fax this ${formTitle}.`}
          disabled={false}
          ></button>) 
      }

      {
        Privilege.ALL ||  Privilege.EXPORT ?
        <button 
          id="btnSaveAsPdf" 
          className="ibutton bsavedoc" 
          title="Save to Document"
          onClick={onSaveDocument}
        ></button>
        :
        <button 
          id="btnPdf" 
          className="ibutton bsace disabled" 
          title={`No privileges to save this ${formTitle} as pdf.`}
          disabled={true}
          ></button>
      }

      {
        Privilege.ALL ||  Privilege.EXPORT ?
        <button 
          id="btnExportPdf" 
          className="ibutton bexport" 
          title="Download"
          onClick={() => downloadPdf(`${formName}-${fileName}`, printRef, setMessage)}
        ></button>
        :
        <button 
          id="btnPdf" 
          className="ibutton bexport disabled" 
          title={`No privileges to download this ${formTitle} as pdf.`}
          disabled={true}
          ></button>
      }

      {
        phoneNumber && phoneNumber.length==10 && (Privilege.ALL ||  Privilege.CALL ?
        <button 
          id="btnCall" 
          className="ibutton bcall" 
          accessKey="p"
          title="Call (Alt+C)"
          onClick={onCallClicked}
        ></button>
        :
        <button 
          id="btnCall" 
          className="ibutton bcall disabled" 
          title={`No privileges to call this client.`}
          disabled={false}
          ></button>)
      }

      <div 
        className="print-head">
        {userName ? userName : ""} 
        &nbsp; 
        <span className='copy'>{clientData?.client_number ? `#${clientData?.client_number}`: ''}</span> 
        &nbsp; 
        {formTitle.toUpperCase()}
      </div>

      {children}

    </div>
)};


export const Button = {
  Action: ActionButton,
  Icon: IconButton,
  FrameAction: FrameActionButton,
  AutoGenerateButton: AutoGenerateButton,
  Link: LinkButton,
  Create: CreateButton,
  Delete: DeleteButton,
  Top: TopButtons,
  Save: SaveButtons,
  Print: PrintButtons,
}